import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  PersonOutline as PersonIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  BadgeOutlined as IdIcon,
  CakeOutlined as BirthdayIcon,
  SaveOutlined as SaveIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError } from '../../utils/sweetAlertConfig';
import CandidateHeader from '../../components/CandidateHeader';
import NationalIdInput from '../../components/NationalIdInput';
import CVUpload from '../../components/CVUpload';
import ProfilePictureUpload from '../../components/ProfilePictureUpload';

const CandidateDashboardNew = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [ssnError, setSsnError] = useState('');

  useEffect(() => {
    loadCandidateData();
  }, []);

  const loadCandidateData = async () => {
    try {
      const response = await api.get('/candidate/profile');
      const candidateData = response.data.candidate || response.data;

      // Map national_id to ssn if needed
      if (candidateData.national_id && !candidateData.ssn) {
        candidateData.ssn = candidateData.national_id;
      }

      // Convert mobile to string
      if (candidateData.mobile !== undefined && candidateData.mobile !== null) {
        candidateData.mobile = candidateData.mobile.toString();
      }

      // Extract birth date and gender from SSN if available
      if (candidateData.ssn && candidateData.ssn.length === 14) {
        const { birthDate, gender } = extractBirthDateAndGenderFromSSN(candidateData.ssn);
        if (birthDate) {
          candidateData.birth_date = birthDate;
          candidateData.age = calculateAge(birthDate);
          candidateData.gender = gender;
        }
      }

      setCandidate(candidateData);
      setFormData(candidateData);
    } catch (error) {
      console.error('Error loading candidate data:', error);
      showError('Error', 'Failed to load your profile data.');
      if (error.response?.status === 401) {
        localStorage.removeItem('candidate_token');
        localStorage.removeItem('candidate_data');
        navigate('/candidate/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const extractBirthDateAndGenderFromSSN = (ssn) => {
    if (!ssn || ssn.length !== 14 || !/^[23]\d{13}$/.test(ssn)) {
      setSsnError('Please enter a valid 14-digit Egyptian National ID.');
      return { birthDate: null, gender: null };
    }

    try {
      const century = ssn[0];
      const yearDigits = ssn.substring(1, 3);
      const month = ssn.substring(3, 5);
      const day = ssn.substring(5, 7);
      const genderDigit = parseInt(ssn[12]);

      const monthNum = parseInt(month);
      const dayNum = parseInt(day);
      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        setSsnError('Invalid date in National ID (month or day out of range).');
        return { birthDate: null, gender: null };
      }

      const year = century === '2' ? `19${yearDigits}` : `20${yearDigits}`;
      const birthDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      const dateObj = new Date(birthDate);
      if (
        dateObj.getFullYear() !== parseInt(year) ||
        dateObj.getMonth() + 1 !== monthNum ||
        dateObj.getDate() !== dayNum
      ) {
        setSsnError('Invalid date format in National ID.');
        return { birthDate: null, gender: null };
      }

      const gender = genderDigit % 2 === 0 ? 'Female' : 'Male';

      setSsnError('');
      return { birthDate, gender };
    } catch (error) {
      setSsnError('Error processing National ID. Please check the input.');
      return { birthDate: null, gender: null };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'ssn' && value && !/^\d*$/.test(value)) {
      setSsnError('National ID must contain only numbers.');
      return;
    }

    let updatedFormData = { ...formData, [name]: value };

    if (name === 'ssn') {
      setSsnError('');
      if (value.length === 14) {
        const { birthDate, gender } = extractBirthDateAndGenderFromSSN(value);
        if (birthDate) {
          updatedFormData.birth_date = birthDate;
          updatedFormData.age = calculateAge(birthDate);
          updatedFormData.gender = gender;
        } else {
          updatedFormData.birth_date = '';
          updatedFormData.age = '';
          updatedFormData.gender = '';
        }
      } else {
        updatedFormData.birth_date = '';
        updatedFormData.age = '';
        updatedFormData.gender = '';
        if (value.length > 0) {
          setSsnError('National ID must be exactly 14 digits.');
        }
      }
    }

    if (name === 'birth_date' && value) {
      updatedFormData.age = calculateAge(value);
    }

    setFormData(updatedFormData);
  };

  const handleSave = async () => {
    if (ssnError) {
      showError('Validation Error', ssnError);
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/candidate/profile', formData);
      setCandidate(response.data.candidate || response.data);
      showSuccess('Success', 'Your profile has been updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        showError('Validation Error', errorMessages.join('\n'));
      } else {
        showError('Error', 'Failed to update your profile. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading your profile...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (!candidate) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Unable to load profile
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please try logging in again.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => navigate('/candidate/login')}
            >
              Back to Login
            </Button>
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        }}
      >
        <CandidateHeader candidate={candidate} />

        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
          {/* Welcome Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#29204C',
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
              }}
            >
              Welcome, {candidate.full_name || 'Candidate'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete your profile information below
            </Typography>
          </Box>

          {/* Profile Form */}
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <PersonIcon sx={{ fontSize: '1.75rem' }} />
                Complete Your Profile Information
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <Grid container spacing={3}>
                  {/* Personal Information Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: '#29204C',
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      Personal Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  {/* First Name */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      name="first_name"
                      label="First Name"
                      value={formData.first_name || ''}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  {/* Middle Name */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      name="middle_name"
                      label="Middle Name"
                      value={formData.middle_name || ''}
                      onChange={handleInputChange}
                      placeholder="Enter middle name(s)"
                      variant="outlined"
                      helperText="You can enter multiple middle names"
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      name="last_name"
                      label="Last Name"
                      value={formData.last_name || ''}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  {/* Contact Information Section */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: '#29204C',
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Grid>

                  {/* Mobile */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="mobile"
                      label="Mobile Number (WhatsApp)"
                      type="tel"
                      value={formData.mobile || ''}
                      onChange={handleInputChange}
                      placeholder="01XXXXXXXXX"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                      helperText="This number will be used for WhatsApp communication"
                    />
                  </Grid>

                  {/* Official Information Section */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: '#29204C',
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      Official Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  {/* National ID - 14 separate inputs */}
                  <Grid item xs={12}>
                    <NationalIdInput
                      value={formData.ssn || ''}
                      onChange={handleInputChange}
                      error={ssnError}
                      disabled={saving}
                    />
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" disabled>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        name="gender"
                        value={formData.gender || ''}
                        label="Gender"
                      >
                        <MenuItem value="">
                          <em>Select Gender</em>
                        </MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Birth Date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="birth_date"
                      label="Birth Date"
                      type="date"
                      value={formData.birth_date || ''}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        readOnly: true,
                        startAdornment: <BirthdayIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          backgroundColor: 'grey.50',
                          cursor: 'not-allowed',
                        },
                      }}
                    />
                  </Grid>

                  {/* Documents Section */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: '#29204C',
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      Documents & Photo
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                  </Grid>

                  {/* Profile Picture Upload */}
                  <Grid item xs={12} md={6}>
                    <ProfilePictureUpload
                      candidateId={candidate?.candidate_id}
                      existingPicture={
                        candidate?.profile_picture_url
                          ? {
                              url: candidate.profile_picture_url,
                              path: candidate.profile_picture_path,
                            }
                          : null
                      }
                      onUploadSuccess={(data) => {
                        setCandidate({
                          ...candidate,
                          profile_picture_url: data?.url,
                          profile_picture_path: data?.path,
                        });
                      }}
                    />
                  </Grid>

                  {/* CV Upload */}
                  <Grid item xs={12} md={6}>
                    <CVUpload
                      candidateId={candidate?.candidate_id}
                      existingCV={
                        candidate?.cv_url
                          ? {
                              url: candidate.cv_url,
                              path: candidate.cv_path,
                              original_name: candidate.cv_original_name,
                            }
                          : null
                      }
                      onUploadSuccess={(data) => {
                        setCandidate({
                          ...candidate,
                          cv_url: data?.url,
                          cv_path: data?.path,
                          cv_original_name: data?.original_name,
                        });
                      }}
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'center',
                        gap: 2,
                        mt: 3,
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={
                          saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                        }
                        disabled={saving || !!ssnError}
                        sx={{
                          minWidth: 200,
                          py: 1.5,
                          px: 4,
                          fontSize: '1rem',
                          fontWeight: 600,
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Profile'}
                      </Button>

                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/candidate/dashboard')}
                        sx={{
                          minWidth: 200,
                          py: 1.5,
                          px: 4,
                          fontSize: '1rem',
                          fontWeight: 600,
                        }}
                      >
                        View Assessments
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Information Alert */}
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }} icon={<InfoIcon />}>
            <Typography variant="body2">
              Please ensure all information is accurate. Your National ID will be used to automatically determine your birth date and gender.
            </Typography>
          </Alert>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CandidateDashboardNew;
